<template>
  <transition name="modal">
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">

          <!-- Header -->
          <div class="modal-header">
            <h1 class="modal-title fs-5">{{ $t("reset.title") }}</h1>
            <button type="button" class="btn-close" aria-label="Close" @click="handleClose"></button>
          </div>

          <!-- Body -->
          <div class="modal-body">

            <!-- Step 1: Email -->
            <template v-if="step === 1">
              <p class="mb-3">{{ $t("reset.step1.description") }}</p>

              <div class="mb-3">
                <input
                  type="email"
                  class="form-control"
                  v-model="email"
                  autocomplete="username"
                  required
                  :placeholder="$t('accountCommon.emailPlaceholder')"
                  :class="{
                    'is-invalid':
                      emailFormatError || emailEmptyError || emailNotFoundError
                  }"
                />

                <div v-if="emailFormatError" class="invalid-feedback d-block">
                  {{ $t("reset.step1.errors.invalidFormat") }}
                </div>
                <div v-if="emailEmptyError" class="invalid-feedback d-block">
                  {{ $t("reset.step1.errors.empty") }}
                </div>
                <div v-if="emailNotFoundError" class="invalid-feedback d-block">
                  {{ $t("reset.step1.errors.notFound") }}
                </div>
              </div>

              <div class="d-grid">
                <button @click.prevent="submitEmail" class="btn btn-primary" type="submit">
                  {{ $t("accountCommon.submitBtn") }}
                </button>
              </div>
            </template>

            <!-- Step 2: Code + Password -->
            <template v-else-if="step === 2">
              <!-- Code -->
              <div class="mb-2">{{ $t("accountCommon.verificationCode") }}</div>
              <div class="mb-3">
                <input
                  id="verificationCode"
                  type="text"
                  class="form-control"
                  v-model="authResetCode"
                  autocomplete="one-time-code"
                  required
                  :placeholder="$t('accountCommon.codePlaceholder')"
                  :class="{ 'is-invalid': codeMismatchError }"
                />
                <div v-if="codeMismatchError" class="invalid-feedback d-block">
                  {{ $t("reset.step2.codeMismatch") }}
                </div>
              </div>

              <!-- New password -->
              <div class="mb-2">
                <div class="input-group">
                  <input
                    :type="isPasswordVisible ? 'text' : 'password'"
                    v-model="password"
                    autocomplete="new-password"
                    class="form-control"
                    required
                    :placeholder="$t('accountCommon.passwordPlaceholder')"
                    :class="{
                      'is-invalid': passwordValidationError || passwordMismatchError
                    }"
                  />
                  <button type="button" class="btn btn-outline-secondary" @click="togglePasswordVisibility">
                    {{ isPasswordVisible ? $t("accountCommon.hidePassword") : $t("accountCommon.showPassword") }}
                  </button>
                </div>

                <div v-if="passwordValidationError" class="invalid-feedback d-block">
                  {{ $t("reset.step2.errors.rule") }}
                </div>
                <div v-if="passwordMismatchError" class="invalid-feedback d-block">
                  {{ $t("reset.step2.errors.mismatch") }}
                </div>
              </div>

              <!-- Password rules list (stabilized zone) -->
              <ul class="list-unstyled small mt-2 mb-3">
                <li :class="password.length >= 8 ? 'text-success' : 'text-secondary'">
                  &#128900; {{ $t("accountCommon.passwordRules.minLength") }}
                </li>
                <li :class="hasNumber ? 'text-success' : 'text-secondary'">
                  &#128900; {{ $t("accountCommon.passwordRules.number") }}
                </li>
              </ul>

              <!-- Repeat password -->
              <div class="mb-3">
                <div class="input-group">
                  <input
                    :type="isRepeatPasswordVisible ? 'text' : 'password'"
                    v-model="repeatPassword"
                    autocomplete="new-password"
                    class="form-control"
                    required
                    :placeholder="$t('accountCommon.repeatPasswordPlaceholder')"
                    :class="{ 'is-invalid': passwordMismatchError }"
                  />
                  <button type="button" class="btn btn-outline-secondary" @click="toggleRepeatPasswordVisibility">
                    {{ isRepeatPasswordVisible ? $t("accountCommon.hidePassword") : $t("accountCommon.showPassword") }}
                  </button>
                </div>
              </div>

              <p class="small text-secondary">
                {{ $t("accountCommon.noEmail") }}
                <button type="button" class="btn btn-link p-0 align-baseline" @click="resendCode">
                  {{ $t("accountCommon.resend") }}
                </button>
              </p>

              <div class="d-grid">
                <button class="btn btn-primary" @click="submitPassword" type="submit">
                  {{ $t("accountCommon.completeBtn") }}
                </button>
              </div>
            </template>

            <!-- Step 3: Complete -->
            <template v-else-if="step === 3">
              <div class="text-center py-4">
                <div class="alert alert-success" role="alert">
                  {{ $t("reset.step3.successMessage") }}
                </div>
                <button @click="handleClose" class="btn btn-primary" type="button">
                  {{ $t("accountCommon.loginBtn") }}
                </button>
              </div>
            </template>
          </div>

          <div class="modal-footer">
            <slot name="footer"></slot>
          </div>

        </div>
      </div>
    </div>
  </transition>

  <div class="modal-backdrop fade show"></div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, nextTick, watch } from "vue";
import { logDebug, logError } from "@/utils/logHelper";
import userApi from "@/common/api/userApi";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const emits = defineEmits(["close"]);

const email = ref("");
const password = ref("");
const repeatPassword = ref("");
const authResetCode = ref("");

const step = ref(1);
const loading = ref(false);

const isPasswordVisible = ref(false);
const isRepeatPasswordVisible = ref(false);

const emailFormatError = ref(false);
const emailEmptyError = ref(false);
const emailNotFoundError = ref(false);
const passwordValidationError = ref(false);
const passwordMismatchError = ref(false);
const codeMismatchError = ref(false);

const { proxy } = getCurrentInstance();

/* ======================================================================================
 * Validators
 * ==================================================================================== */
const hasNumber = computed(() => /\d/.test(password.value));

const isPasswordValid = computed(() =>
  password.value.length >= 8 && hasNumber.value
);

/* ======================================================================================
 * Step Change → Autofocus
 * ==================================================================================== */
watch(step, async (v) => {
  if (v === 2) {
    await nextTick();
    document.querySelector("#verificationCode")?.focus();
  }
});

/* ======================================================================================
 * Utils
 * ==================================================================================== */
function resetErrors() {
  emailFormatError.value = false;
  emailEmptyError.value = false;
  emailNotFoundError.value = false;
  passwordValidationError.value = false;
  passwordMismatchError.value = false;
  codeMismatchError.value = false;
}

function handleClose() {
  emits("close");
}

function togglePasswordVisibility() {
  isPasswordVisible.value = !isPasswordVisible.value;
}

function toggleRepeatPasswordVisibility() {
  isRepeatPasswordVisible.value = !isRepeatPasswordVisible.value;
}

/* ======================================================================================
 * STEP 1: Request verification code
 * ==================================================================================== */
function validateEmailFormat(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
}

function submitEmail() {
  resetErrors();

  if (!email.value) {
    emailEmptyError.value = true;
    return;
  }

  if (!validateEmailFormat(email.value)) {
    emailFormatError.value = true;
    return;
  }

  reqSignupCode();
}

function reqSignupCode() {
  loading.value = true;

  userApi
    .reqcode({ userEmail: email.value })
    .then(() => {
      proxy.$toast?.success(t("reset.step1.codeSent"));
      step.value = 2;
    })
    .catch((error) => {
      emailNotFoundError.value = true;
      const msg = error.errorResponse?.message || "Code request failed";
      proxy.$toast?.error(msg);
    })
    .finally(() => {
      loading.value = false;
    });
}

/* ======================================================================================
 * STEP 2: Submit new password
 * ==================================================================================== */
function submitPassword() {
  resetErrors();

  if (!isPasswordValid.value) {
    passwordValidationError.value = true;
    return;
  }

  if (password.value !== repeatPassword.value) {
    passwordMismatchError.value = true;
    return;
  }

  completeSignup();
}

function completeSignup() {
  loading.value = true;

  userApi
    .resetPassword({
      userEmail: email.value,
      newPassword: password.value,
      authCode: authResetCode.value,
    })
    .then(() => {
      proxy.$toast?.success(t("reset.step3.successMessage"));
      step.value = 3;
    })
    .catch((error) => {
      const msg = error.errorResponse?.message || "Reset failed";

      if (msg.includes("Invalid") || msg.includes("Code")) {
        codeMismatchError.value = true;
      }

      proxy.$toast?.error(msg);
    })
    .finally(() => {
      loading.value = false;
    });
}

/* ======================================================================================
 * Resend code
 * ==================================================================================== */
function resendCode() {
  userApi
    .reqcode({ userEmail: email.value })
    .then(() => proxy.$toast?.success(t("signup.resendSuccess")))
    .catch((error) => {
      const msg = error.errorResponse?.message || "Resend failed";
      proxy.$toast?.error(msg);
    });
}
</script>
